// src/components/goals/MonthClosePrompt.js
//
// "Close September?" for admins, from the 1st of a month while last month is
// unclosed and had goal counts or weekly reports. Goal progress for the new
// month has already started fresh (counts are keyed by month); this only asks
// the admin to record last month's final snapshot:
//   Close now  -> the same close as the Coordinator page's button
//   Later      -> hidden for the rest of today (per user, company, site, month)
// If last month still has goal points waiting for approval, it says so first.
// Shown on Admin Reports and the Coordinator page. Uses the weekly-report
// pop-up shell (WrModal), which already has light- and dark-mode rules.

import { useEffect, useState } from "react";
import { CalendarCheck, AlertTriangle, Loader2, CheckCircle2 } from "lucide-react";
import { auth } from "../../firebase";
import { useTenant } from "../../context/TenantContext";
import { monthLabel } from "../../services/enrollmentPeriods";
import {
  previousMonthId,
  shouldPromptClose,
  closePromptKey,
  readSnoozedDay,
  writeSnoozedDay,
} from "../../services/monthClosePrompt";
import { loadMonthCloseStatus, loadMonthCloseRows, writeMonthClose } from "../../services/goalMonthClose";
import { loadGoalPointData } from "../../services/goalPointService";
import { buildGoalPointCards } from "../../services/goalRules";
import { WrModal, BTN_PRIMARY, BTN_SOFT } from "../weeklyReport/ui";

const MonthClosePrompt = ({ onClosed, onReview }) => {
  const { user, isOwner, isTenantAdmin, roleForActiveSite, tenantId, siteId } = useTenant();
  const isAdmin = !!(isOwner || isTenantAdmin?.(tenantId) || roleForActiveSite === "admin");
  const uid = user?.uid || auth.currentUser?.uid || "";
  const monthId = previousMonthId(new Date());
  const label = monthLabel(monthId) || monthId;
  const key = closePromptKey(uid, tenantId, siteId, monthId);

  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // { total, screeningTotal }
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isAdmin || !uid) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const status = await loadMonthCloseStatus(monthId);
        if (cancelled) return;
        if (!shouldPromptClose({ ...status, snoozedDay: readSnoozedDay(key), now: new Date() })) return;
        // Points still waiting for that month (fail-soft: the prompt shows anyway).
        try {
          const data = await loadGoalPointData();
          if (!cancelled) setPending(buildGoalPointCards(data).filter((c) => c.monthId === monthId && c.newPoints > 0).length);
        } catch {
          /* ignore */
        }
        if (!cancelled) setOpen(true);
      } catch (err) {
        console.error("❌ Could not check last month's close:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdmin, uid, monthId, key]);

  const later = () => {
    writeSnoozedDay(key, new Date());
    setOpen(false);
  };

  const closeNow = async () => {
    setBusy(true);
    setError("");
    try {
      const rows = await loadMonthCloseRows(monthId);
      const totals = await writeMonthClose({ monthId, ...rows, by: auth.currentUser?.email || "" });
      setDone(totals);
      onClosed?.(monthId, totals);
    } catch (err) {
      console.error("❌ Could not close the month:", err);
      setError("Couldn't close the month. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!isAdmin) return null;

  return (
    <WrModal
      open={open}
      onClose={() => (done ? setOpen(false) : !busy && later())}
      icon={CalendarCheck}
      title={done ? `${label} closed` : `Close ${label}?`}
      subtitle={done ? "" : "Goal progress for this month has already started fresh."}
      footer={
        done ? (
          <button type="button" className={BTN_PRIMARY} onClick={() => setOpen(false)}>
            Done
          </button>
        ) : (
          <>
            <button type="button" className={`${BTN_SOFT} mr-auto`} onClick={later} disabled={busy}>
              Later
            </button>
            <button type="button" className={BTN_PRIMARY} onClick={closeNow} disabled={busy}>
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              Close {label} now
            </button>
          </>
        )
      }
    >
      {done ? (
        <p className="flex items-start gap-2 text-sm text-slate-300">
          <CheckCircle2 className="month-close-ok w-4 h-4 shrink-0 mt-0.5 text-emerald-300" />
          <span>
            Recorded {done.total} randomization(s) and {done.screeningTotal} screening(s) for {label}. No
            counts were changed.
          </span>
        </p>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-slate-300">
            Closing records {label}'s final numbers for both goals. Counts aren't reset or changed, and you can still
            edit {label} later.
          </p>
          {pending > 0 && (
            <div className="weekly-report-warn flex flex-wrap items-center gap-2 rounded-lg border border-amber-400/30 bg-amber-500/10 px-3 py-2 text-[12px] text-amber-200">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
              <span className="flex-1 min-w-0">
                {pending} goal point card{pending === 1 ? " is" : "s are"} still waiting for approval for {label}.
                Approve them first so they're in the snapshot.
              </span>
              {onReview && (
                <button
                  type="button"
                  className="month-close-review underline font-semibold"
                  onClick={() => {
                    setOpen(false);
                    onReview();
                  }}
                >
                  Review them
                </button>
              )}
            </div>
          )}
          <p className="text-[11px] text-slate-400">"Later" asks again tomorrow. The Close button on the Coordinator page still works too.</p>
          {error && (
            <p className="weekly-report-error rounded-lg border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-[12px] text-rose-200">
              {error}
            </p>
          )}
        </div>
      )}
    </WrModal>
  );
};

export default MonthClosePrompt;
